import { T, useTheme, Ico, Card, Eyebrow, Btn, Badge, StatusBadge } from '../../../design';
import type { IconName } from '../../../design';
import type { Course, MyEnrollment } from '../../../types/courses.types';
import { fmtDate, fmtPrice, fmtTime, sanitizeRichText } from '../../../utils';
import { availInfo, CourseImage, courseImages } from '../courseUtils';

function MetaRow({ icon, label, value }: { icon: IconName; label: string; value: string }) {
  const Ic = Ico[icon];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
      <span style={{ color: T.fg3, marginTop: 1, flexShrink: 0 }}><Ic size={17} /></span>
      <div><div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.fg3 }}>{label}</div><div style={{ fontSize: 14, fontWeight: 700, color: T.fg, marginTop: 2 }}>{value}</div></div>
    </div>
  );
}

export interface CourseDetailProps {
  course: Course;
  role: 'owner' | 'member';
  myEnrollment: MyEnrollment | null;
  onEnroll: (course: Course) => void;
  onBulk: (course: Course) => void;
}

export function CourseDetail({ course, role, myEnrollment, onEnroll, onBulk }: CourseDetailProps) {
  const t = useTheme();
  const av = availInfo(course);
  const me = myEnrollment;
  const enrolledActive = !!me && (me.status === 'pending' || me.status === 'approved');
  const images = courseImages(course);
  const slots = images.length > 0 ? images : [null];

  let cta;
  if (av.disabled) cta = <Btn full size="lg" disabled>Kurset er afsluttet</Btn>;
  else if (enrolledActive) cta = <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', borderRadius: t.radius, background: T.accentLight, color: t.accent, fontSize: 14, fontWeight: 700 }}><Ico.checkCircle size={18} />{me!.status === 'approved' ? 'Du er tilmeldt' : 'Afventer godkendelse'}</div>;
  else if (av.full) cta = <Btn full size="lg" disabled>Fuldt booket</Btn>;
  else cta = <Btn full size="lg" icon={<Ico.plus size={19} />} onClick={() => onEnroll(course)}>Tilmeld {role === 'owner' ? '' : 'mig'}</Btn>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: role === 'owner' && !av.disabled ? 92 : 16 }}>
      <div style={{ display: 'flex', gap: 9, overflowX: 'auto', paddingBottom: 2, margin: '0 -2px' }}>
        {slots.map((src, i) => <div key={i} style={{ width: 150, flexShrink: 0 }}><CourseImage src={src} alt={course.productName} height={188} /></div>)}
      </div>
      <div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}><Badge tone={av.tone} dot>{av.label}</Badge>{course.courseType === 'external' && <Badge tone="neutral">Ekstern</Badge>}{me && <StatusBadge status={me.status} />}</div>
        <h1 style={{ fontSize: 23, fontWeight: 800, color: T.fg, letterSpacing: '-0.02em', margin: 0, lineHeight: 1.15 }}>{course.productName}</h1>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.accent, marginTop: 8 }}>{fmtPrice(course.price)}</div>
      </div>

      <Card pad={16}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 12px' }}>
          <MetaRow icon="clock" label="Dato" value={fmtDate(course.courseDate)} />
          <MetaRow icon="clock" label="Tid" value={course.startTime ? `${fmtTime(course.startTime)}${course.endTime ? `–${fmtTime(course.endTime)}` : ''}` : 'Oplyses senere'} />
          <MetaRow icon="store" label="Lokation" value={course.location || 'Oplyses senere'} />
          <MetaRow icon="receipt" label="Afmelding" value={`Indtil ${course.cancellationHours}t før`} />
        </div>
      </Card>

      {course.description && (
        <div>
          <Eyebrow style={{ marginBottom: 8 }}>Om kurset</Eyebrow>
          <div className="course-rich-text" style={{ fontSize: 14, color: T.fg2, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: sanitizeRichText(course.description) }} />
        </div>
      )}

      {course.instructors.length > 0 && (
        <div>
          <Eyebrow style={{ marginBottom: 8 }}>Instruktør</Eyebrow>
          {course.instructors.map((ins) => (
            <div key={ins.id} style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 8 }}>
              <span style={{ width: 40, height: 40, borderRadius: 999, background: T.accentLight, color: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>{ins.name.split(' ').map((s) => s[0]).slice(0, 2).join('')}</span>
              <div><div style={{ fontSize: 14, fontWeight: 700, color: T.fg }}>{ins.name}</div>{ins.description && <div className="course-rich-text" style={{ fontSize: 12, color: T.fg3, fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: sanitizeRichText(ins.description) }} />}</div>
            </div>
          ))}
        </div>
      )}

      {!enrolledActive && role !== 'owner' && <div style={{ marginTop: 4 }}>{cta}</div>}
      {!enrolledActive && role !== 'owner' && me && me.status === 'rejected' && <div style={{ fontSize: 12, color: T.error, fontWeight: 600, textAlign: 'center', display: 'flex', gap: 6, justifyContent: 'center' }}><Ico.info size={14} />Din tidligere tilmelding blev afvist — du kan tilmelde dig igen.</div>}
      {enrolledActive && <div style={{ marginTop: 4 }}>{cta}</div>}
      {av.disabled && <div style={{ marginTop: 4 }}>{cta}</div>}

      {role === 'owner' && !av.disabled && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30, background: T.surface, borderTop: `1px solid ${T.border}`, padding: '12px 14px calc(12px + env(safe-area-inset-bottom))', display: 'flex', gap: 10, maxWidth: 440, margin: '0 auto' }}>
          {!enrolledActive && !av.full ? <Btn size="lg" onClick={() => onEnroll(course)} style={{ flex: 1 }} icon={<Ico.plus size={18} />}>Tilmeld mig</Btn> : <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: t.accent }}>{enrolledActive ? 'Du er tilmeldt' : 'Fuldt'}</div>}
          <Btn variant="secondary" size="lg" onClick={() => onBulk(course)} style={{ flex: 1 }} icon={<Ico.users size={18} />}>Tilmeld hold</Btn>
        </div>
      )}
    </div>
  );
}
